/**
 * Audit trail panel for the Price Book Sharing configuration — CR-6346 rework.
 *
 * QA rejected the first pass with "no audit trail or timestamp showing when the
 * configuration was created or changed". The UserActivityLog rows were being
 * written server-side all along; nothing surfaced them. This is that surface:
 * an expandable history of who changed the setting, when, and from what to what.
 *
 * Loaded lazily on first expand so the settings page keeps its current cost for
 * admins who never open it.
 */
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Loader2, History, ChevronDown, ChevronRight, ArrowRight, AlertCircle } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import type { PriceBookSharingHistoryRow } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

export function PriceBookSharingHistory({ refreshKey = 0 }: { refreshKey?: number }) {
  const { formatDateTime } = useFormatters();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<PriceBookSharingHistoryRow[] | null>(null);
  const [loadedKey, setLoadedKey] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformSettingsAPI.getPriceBookSharingHistory(20);
      setRows(res.data.results ?? []);
      setLoadedKey(refreshKey);
    } catch (err: unknown) {
      console.error('Error loading price book sharing history:', err);
      setError('Failed to load change history');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    // Reload when opening for the first time, or after a save changed things.
    if (next && (rows === null || loadedKey !== refreshKey)) {
      void load();
    }
  };

  return (
    <div className="rounded-md border border-input">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <History className="h-4 w-4 text-muted-foreground" />
        Change history
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          Who changed this, and when
        </span>
      </button>

      {open && (
        <div className="border-t border-input px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : !rows || rows.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              No changes recorded yet. The next change to price book sharing will
              be listed here with the user, timestamp and what changed.
            </p>
          ) : (
            <ol className="space-y-3">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="border-l-2 border-muted pl-3 text-sm"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">{row.user_name}</span>
                    <span className="text-muted-foreground">{row.action.toLowerCase()}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(row.timestamp)}
                    </span>
                  </div>
                  <ul className="mt-1 space-y-1">
                    {row.entries.map((entry, i) => (
                      <li
                        key={`${row.id}-${i}`}
                        className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
                      >
                        <span className="font-medium text-foreground">{entry.field}</span>
                        <span className="rounded bg-muted px-1.5 py-0.5">{entry.old}</span>
                        <ArrowRight className="h-3 w-3" />
                        <span className="rounded bg-muted px-1.5 py-0.5 text-foreground">
                          {entry.new}
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
